import type {
  AdminLegalDocumentVersionDTO,
  LegalDocumentKey,
  LegalDocumentPublishInput,
  LegalDocumentUploadMetadata,
} from "@ai-crm/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi, ApiError } from "./api";
import { usePaymentScope, usePaymentWrite } from "./payment-ui";

export const legalDocumentKeys = {
  catalog: (adminId: string) => ["admin", adminId, "legal-documents"] as const,
  history: (adminId: string, key: LegalDocumentKey) =>
    ["admin", adminId, "legal-document-versions", key] as const,
};

export function useLegalDocumentCatalog() {
  const scope = usePaymentScope();
  return useQuery({
    queryKey: legalDocumentKeys.catalog(scope.adminId),
    queryFn: ({ signal }) => scope.read(signal, () => adminApi.legalDocuments(scope.token!, signal)),
    enabled: Boolean(scope.token),
  });
}

export function useLegalDocumentHistory(key: LegalDocumentKey, cursor?: string) {
  const scope = usePaymentScope();
  return useQuery({
    queryKey: [...legalDocumentKeys.history(scope.adminId, key), cursor ?? ""],
    queryFn: ({ signal }) => scope.read(signal, () =>
      adminApi.legalDocumentVersions(scope.token!, key, { cursor, limit: 10 }, signal)),
    enabled: Boolean(scope.token),
  });
}

/** The shared payment write guard retains the original body after an uncertain reply. */
export function useLegalDocumentWrite(key: LegalDocumentKey) {
  const scope = usePaymentScope();
  const write = usePaymentWrite();
  const client = useQueryClient();
  async function refresh(includeCatalog: boolean, guard: ReturnType<typeof scope.capture>) {
    if (!guard.isCurrent()) return;
    await client.invalidateQueries({ queryKey: legalDocumentKeys.history(scope.adminId, key) });
    if (includeCatalog && guard.isCurrent()) {
      await client.invalidateQueries({ queryKey: legalDocumentKeys.catalog(scope.adminId) });
    }
  }
  return {
    ...write,
    async upload(
      file: File,
      metadata: LegalDocumentUploadMetadata,
      accepted: (version: AdminLegalDocumentVersionDTO) => void,
    ) {
      if (!scope.token) return;
      const guard = scope.capture(), token = scope.token;
      let version: AdminLegalDocumentVersionDTO | undefined;
      await write.run(
        { file, metadata },
        async (body) => { version = await adminApi.uploadLegalDocument(token, key, body.metadata, body.file); },
        async () => {
          if (!guard.isCurrent() || !version) return;
          accepted(version);
          await refresh(false, guard);
        },
        async () => { await refresh(false, guard); },
      );
    },
    async publish(
      input: LegalDocumentPublishInput,
      accepted: () => void,
      conflicted: () => void,
    ) {
      if (!scope.token) return;
      const guard = scope.capture(), token = scope.token;
      await write.run(input,
        (body) => adminApi.publishLegalDocument(token, key, body),
        async () => {
          // Replay may describe an older publication: always refetch the live catalog.
          await refresh(true, guard);
          if (guard.isCurrent()) accepted();
        },
        async (error) => {
          if (error instanceof ApiError && error.status === 409 && guard.isCurrent()) {
            conflicted();
            await refresh(true, guard);
          }
        },
      );
    },
  };
}
